/**
 * Download range (C2; TMMAPP-0108 / TMMAPP-0111; audit 06 § A2 / C2, v2 L2230–2260, L4320–4330): one
 * dialog for the three downloads — "Download transactions", "Download P&L", "Download tax report" — each with
 * its sub-line, "Range" with the P&L view's five preset chips (TMMAPP-0113 4e: one vocabulary) — Year
 * to date · Last 12 months · This month · Last month · Last year — then From / To ("The start date is after the end date."), Cancel /
 * Download. It opens on the last 12 months, or the year to date for tax. Transactions are the register
 * CSV (`GET …/exports/register?from&to`); the P&L is the virtual account workbook, whose first sheet is
 * the P&L by category (`GET …/export.xlsx?from&to`; TMMAPI-0147 7c); the tax report is
 * `GET /v1/me/exports/tax-report.xlsx?from&to&virtualAccountId` — without a virtual account (Settings) it
 * spans every readable one. The server names each file.
 */
import { useId, useState } from 'react';
import {
  Box,
  Button,
  ButtonBase,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { DownloadResult } from '@/api/client';
import { exportsApi } from '@/api/exports';
import { virtualAccountsApi, type VirtualAccountDetail } from '@/api/virtualAccounts';
import { DialogSubtitle } from '@/components/DialogSubtitle';
import { SubmitButton } from '@/components/SubmitButton';
import { todayCivilInZone } from '@/i18n/zonedCivil';
import { useMe } from '@/session/meHooks';
import { useDialogFullScreen } from '@/shell/useDialogFullScreen';
import { resolveTokens } from '@/theme/theme';
import { downloadBlob } from '@/utils/downloadBlob';
import { slug } from '@/utils/slug';
import { presetRange, RANGE_PRESETS, type RangePreset } from './downloadRange';

export type DownloadKind = 'transactions' | 'pnl' | 'tax';

type DownloadRangeDialogProps = {
  onClose: () => void;
  onNotify: (message: string, options?: { severity?: 'error' }) => void;
} & (
  | { kind: 'transactions' | 'pnl'; virtualAccount: Pick<VirtualAccountDetail, 'id' | 'name'> }
  /** The tax report; without a virtual account it covers every readable one (Settings). */
  | { kind: 'tax'; virtualAccount: Pick<VirtualAccountDetail, 'id' | 'name'> | null }
);

/** The file for a kind and a window; the server's `Content-Disposition` names it. */
function fetchDownload(
  kind: DownloadKind,
  virtualAccount: Pick<VirtualAccountDetail, 'id' | 'name'> | null,
  window: { from?: string; to?: string },
): Promise<DownloadResult> {
  if (kind === 'tax') {
    return exportsApi.taxReportXlsx({ ...window, virtualAccountId: virtualAccount?.id });
  }
  const id = virtualAccount?.id ?? 0;
  return kind === 'pnl'
    ? virtualAccountsApi.exportXlsx(id, window)
    : exportsApi.virtualAccountRegisterCsv(id, window);
}

export function DownloadRangeDialog({
  kind,
  virtualAccount,
  onClose,
  onNotify,
}: DownloadRangeDialogProps) {
  const { t } = useTranslation();
  const tokens = resolveTokens(useTheme());
  const me = useMe();
  const fullScreen = useDialogFullScreen();
  const titleId = useId();
  const subtitleId = useId();
  const today = todayCivilInZone(me.user.timezone ?? 'UTC');
  // v2: the last 12 months, or the year to date for tax.
  const initialPreset: RangePreset = kind === 'tax' ? 'ytd' : '12m';
  const [preset, setPreset] = useState<RangePreset | null>(initialPreset);
  const [[from, to], setRange] = useState(() => presetRange(initialPreset, today));
  const [busy, setBusy] = useState(false);
  const bad = from !== '' && to !== '' && from > to;
  const copy = virtualAccount === null ? 'taxAll' : kind;

  async function download() {
    if (bad || busy) return;
    setBusy(true);
    try {
      const { blob, filename } = await fetchDownload(kind, virtualAccount, {
        ...(from ? { from } : {}),
        ...(to ? { to } : {}),
      });
      const base =
        virtualAccount === null ? 'tax-report' : slug(virtualAccount.name, 'virtual-account');
      const fallback =
        kind === 'transactions'
          ? `${base}.csv`
          : kind === 'pnl'
            ? `${base}-pnl.xlsx`
            : virtualAccount === null
              ? `${base}.xlsx`
              : `${base}-tax-report.xlsx`;
      downloadBlob(blob, filename ?? fallback);
      onNotify(t('exports.ready'));
      onClose();
    } catch {
      onNotify(t('exports.failed'), { severity: 'error' });
      setBusy(false);
    }
  }

  return (
    <Dialog
      open
      onClose={busy ? undefined : onClose}
      aria-labelledby={titleId}
      aria-describedby={subtitleId}
      fullWidth
      fullScreen={fullScreen}
      maxWidth="xs"
      sx={{ '& .MuiDialog-paper': { maxWidth: fullScreen ? undefined : 480 } }}
    >
      <DialogTitle id={titleId}>{t(`virtualAccount.download.title.${kind}`)}</DialogTitle>
      <DialogSubtitle id={subtitleId}>
        {t(`virtualAccount.download.sub.${copy}`, { name: virtualAccount?.name ?? '' })}
      </DialogSubtitle>
      <DialogContent>
        <Typography
          id={`${titleId}-range`}
          component="p"
          sx={{
            mt: 2,
            mb: 1,
            fontSize: 12,
            fontWeight: 500,
            letterSpacing: '0.07em',
            textTransform: 'uppercase',
            color: tokens.textLabel('surfaceRaised'),
          }}
        >
          {t('virtualAccount.download.range')}
        </Typography>
        <Box
          role="group"
          aria-labelledby={`${titleId}-range`}
          sx={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}
        >
          {RANGE_PRESETS.map((key) => {
            const on = preset === key;
            return (
              <ButtonBase
                key={key}
                aria-pressed={on}
                onClick={() => {
                  setPreset(key);
                  setRange(presetRange(key, today));
                }}
                sx={{
                  borderRadius: '16px',
                  p: '5px 12px',
                  fontSize: 13,
                  border: `1px solid ${on ? tokens.brandBoundary : tokens.line}`,
                  color: on ? tokens.link : tokens.text2,
                  bgcolor: tokens.surfaceRaised,
                  backgroundImage: on
                    ? `linear-gradient(${tokens.selected}, ${tokens.selected})`
                    : 'none',
                  '&:hover': { borderColor: tokens.brandBoundary },
                  '&.Mui-focusVisible': {
                    outline: `2px solid ${tokens.focusRing}`,
                    outlineOffset: 2,
                  },
                }}
              >
                {t(`virtualAccount.download.preset.${key}`)}
              </ButtonBase>
            );
          })}
        </Box>
        <Box
          sx={{
            mt: 2,
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
            gap: 1.5,
          }}
        >
          <TextField
            type="date"
            label={t('envelope.windowFrom')}
            value={from}
            onChange={(event) => {
              setPreset(null);
              setRange([event.target.value, to]);
            }}
            error={bad}
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <TextField
            type="date"
            label={t('envelope.windowTo')}
            value={to}
            onChange={(event) => {
              setPreset(null);
              setRange([from, event.target.value]);
            }}
            error={bad}
            slotProps={{ inputLabel: { shrink: true } }}
          />
        </Box>
        {bad && (
          <Typography role="alert" sx={{ mt: 1, fontSize: 12, color: tokens.errorText }}>
            {t('virtualAccount.download.badRange')}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          {t('common.cancel')}
        </Button>
        <SubmitButton onClick={() => void download()} disabled={bad} loading={busy}>
          {t('virtualAccount.download.action')}
        </SubmitButton>
      </DialogActions>
    </Dialog>
  );
}
